import { PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { Box, Button, Typography } from '@mui/material';
import useUserData from 'hooks/useUserData';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { CircularProgressWithLabel, ContentModal } from '@buildwise/libs/ui';
import { Link, useNavigate } from 'react-router-dom';
import { DRAFTS } from 'constants/routes';
import CheckboxCircleFillIcon from 'remixicon-react/CheckboxCircleFillIcon';
import { theme } from 'config/theme';
import knowledgeAPI from 'api/KnowledgeAPI';
import { AxiosResponse } from 'axios';
import SelectPostsCountModal from './SelectPostsCountModal';
import { AttachmentType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import { mdToHtml } from '@buildwise/libs/helpers';
import { CircularProgress } from '@mui/material';

type ParseFileActionsProps = {
  downloadedFiles: AttachmentType[];
  isOnePostSelected: boolean;
  response: any;
  onParseFile: (options: { isOnePostSelected: boolean }) => Promise<void>;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setPublishingFileAsOnePost: Dispatch<SetStateAction<boolean>>;
  setResponse: any;
};

function ParseFileActions({
  downloadedFiles = [],
  isOnePostSelected,
  response,
  onParseFile,
  setOpen,
  setPublishingFileAsOnePost,
  setResponse,
}: ParseFileActionsProps) {
  const navigate = useNavigate();
  const { companyId } = useUserData();
  const {
    formState: { isSubmitting },
    resetField,
    setError,
    setValue,
    watch,
  } = useFormContext<PostFormData>();
  const attachments: AttachmentType[] = watch('attachments') || [];
  const [isLoading, setLoading] = useState(false);
  const [isSavingDrafts, setSavingDrafts] = useState(false);
  const [isPublishingPosts, setPublishingPosts] = useState(false);
  const [isCompleted, setCompleted] = useState(false);
  const [openPostsCountModal, setOpenPostsCountModal] = useState(false);
  const [progressValues, setProgressValues] = useState<Record<string, number>>({});
  const avgProgressValue = useMemo(() => {
    const filesKeys = Object.keys(progressValues);
    return filesKeys.length > 0
      ? filesKeys.reduce((acc, key) => acc + progressValues[key], 0) / filesKeys.length
      : 0;
  }, [progressValues]);

  const onSubmit = useCallback(() => {
    if (attachments.length) {
      // setOpenPostsCountModal(true);
      onParseFile({ isOnePostSelected: true });
    } else {
      setValue('attachments', [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
      setError(
        'attachments',
        { message: 'Please upload at least one file' },
        { shouldFocus: true },
      );
    }
  }, [attachments]);

  const onNext = useCallback(async () => {
    const { title = '', description = '' } = response?.metadata?.articlesList?.[0] || {};
    setValue('title', title);
    setValue('description', await mdToHtml(description));
    setValue('attachments', [...attachments, ...downloadedFiles]);

    setPublishingFileAsOnePost(true);
  }, [response, attachments, downloadedFiles]);

  const onSaveToDrafts = useCallback(async () => {
    try {
      setSavingDrafts(true);
      const addArticlePromises: Promise<AxiosResponse<any, Error> | any>[] = [];
      response?.metadata?.articlesList?.forEach?.((article) => {
        addArticlePromises.push(knowledgeAPI.addArticle(companyId, article));
      });

      await Promise.all(addArticlePromises);
      navigate(DRAFTS);
    } catch (error) {
      console.log('SAVE_TO_DRAFTS_ERROR', error);
    } finally {
      setSavingDrafts(false);
    }
  }, [response]);

  const onPublishPosts = useCallback(async () => {
    try {
      setPublishingPosts(true);
      const addArticlePromises: Promise<AxiosResponse<any, Error> | any>[] = [];
      response?.metadata?.articlesList?.forEach?.((article) => {
        addArticlePromises.push(
          knowledgeAPI.addArticle(companyId, { ...article, isDraft: false, status: 'PUBLISHED' }),
        );
      });

      await Promise.all(addArticlePromises);
      // TODO: Update cache with all published articles

      setOpen(false);
    } catch (error) {
      console.log('PUBLISH_POSTS_ERROR', error);
    } finally {
      setPublishingPosts(false);
    }
  }, [response]);

  // const onParseFile = useCallback(async () => {
  //   if (attachments.length) {
  //     setLoading(true);
  //     setCompleted(false);
  //     let mappedFiles: FileType[] = [];
  //     mappedFiles = attachments.map((file) => ({
  //       createdAt: moment().toISOString(),
  //       createdBy: user?.username || '',
  //       documentDirectory: 'files',
  //       documentPath: 'files',
  //       documentName: file.fileName,
  //       documentPDF: '',
  //       documentSize: file.fileSize,
  //       documentType: file.fileName.split('.').pop() || '',
  //       documentURL: file.articleOpenUrl || '',
  //       id: uniqueId(),
  //       profileImageURL: user?.profileImageURL || '',
  //       userNameExpYearsToDisplay: user?.expYears || '',
  //       userNameTitleToDisplay: user?.title || '',
  //       userNameToDisplay: user?.userNameToDisplay || '',
  //       file: file.file,
  //       fileMD5Hash: file.fileMD5Hash,
  //     }));

  //     try {
  //       // Upload files meta info
  //       const { data } = await PythonPostsAPI.saveFilesMetaData({
  //         filesList: mappedFiles.map((file) => ({
  //           documentName: file.documentName,
  //           documentDirectory: file.documentDirectory,
  //           documentSize: file.documentSize,
  //           documentType: file.documentType,
  //           projectId: file.projectId,
  //         })),
  //         companyId,
  //         level: 'company',
  //       });

  //       // Upload files to S3
  //       mappedFiles = mappedFiles.map((file) => ({
  //         ...file,
  //         ...data.filesList.find((_file) => _file.documentName === file.documentName),
  //       }));
  //       const filesUploadPromises: Promise<any>[] = [];

  //       mappedFiles.forEach((file) => {
  //         filesUploadPromises.push(
  //           new Promise(async (resolve, reject) => {
  //             try {
  //               if (file.UploadUrl && file.file) {
  //                 await PythonPostsAPI.uploadFileToS3({
  //                   s3Url: file.UploadUrl,
  //                   file: file.file,
  //                   uploadProgress: (uploadedPercentage) => {
  //                     setProgressValues((prevState) => ({
  //                       ...prevState,
  //                       [file.documentName]: uploadedPercentage,
  //                     }));
  //                   },
  //                 });

  //                 // Verify uploaded files are saved
  //                 if (file.fileMD5Hash) {
  //                   await PythonPostsAPI.verifyUploadedFiles({
  //                     id: file.id,
  //                     companyId,
  //                     fileMD5Hash: file.fileMD5Hash,
  //                     MimeType: file.file.type,
  //                     filePath: file.s3Key || '',
  //                   });

  //                   resolve(true);
  //                 }
  //               }
  //             } catch (error) {
  //               console.log('S3_FILE_UPLOAD_ERROR', error);
  //               reject(error);
  //             }
  //           }),
  //         );
  //       });

  //       await Promise.all(filesUploadPromises);
  //       setCompleted(true);
  //       // reset();
  //     } catch (error) {
  //       console.log('PARSE_FILE_ERROR', error);
  //     }
  //   } else {
  //     setValue('attachments', [], { shouldDirty: true, shouldTouch: true, shouldValidate: true });
  //     setError(
  //       'attachments',
  //       { message: 'Please upload at least one file' },
  //       { shouldFocus: true },
  //     );
  //   }
  // }, [attachments, user, companyId]);

  return (
    <>
      {isOnePostSelected && attachments.length > 0 ? (
        <Button
          variant="contained"
          disabled={isSubmitting || response.loading || response.isStreaming}
          onClick={onNext}
        >
          Next
        </Button>
      ) : response?.genAIResponse ? (
        <Box width="100%" display="flex" alignItems="center" justifyContent="space-between">
          <Button
            disabled={isSubmitting || response.isStreaming || isSavingDrafts || isPublishingPosts}
            onClick={onSaveToDrafts}
          >
            {isSavingDrafts ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : (
              'Save to Drafts'
            )}
          </Button>
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              disabled={isSubmitting || response.isStreaming || isSavingDrafts || isPublishingPosts}
              variant="text"
              onClick={() => {
                resetField('attachments');
                setResponse((prevState) => ({
                  ...prevState,
                  genAIResponse: '',
                  metadata: { ...prevState.metadata, title: '', description: '', attachments: [] },
                }));
              }}
            >
              Cancel
            </Button>
            <Button
              disabled={isSubmitting || response.isStreaming || isSavingDrafts || isPublishingPosts}
              variant="contained"
              onClick={onPublishPosts}
            >
              {isPublishingPosts ? (
                <CircularProgress
                  sx={(theme) => ({ color: theme.palette.custom.black })}
                  size={20}
                />
              ) : (
                'Publish Posts'
              )}
            </Button>
          </Box>
        </Box>
      ) : (
        <Button
          disabled={isSubmitting || response.isStreaming}
          variant="contained"
          onClick={onSubmit}
        >
          Create a Draft
        </Button>
      )}

      {isLoading && (
        <ContentModal
          open={isLoading}
          content={
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              {isCompleted ? (
                <>
                  <CheckboxCircleFillIcon size={60} color={theme.palette.custom.greenBlue} />
                  <Typography
                    sx={(theme) => ({
                      mt: 0.5,
                      color: theme.palette.custom.tuna,
                      fontWeight: 700,
                      fontSize: 16,
                    })}
                  >
                    Your {attachments.length > 1 ? 'files have been' : 'file is'} successfully
                    uploaded!
                  </Typography>
                  <Typography
                    sx={(theme) => ({
                      my: 2,
                      color: theme.palette.custom.tuna,
                      fontSize: 16,
                    })}
                  >
                    Our system is now processing the {attachments.length > 1 ? 'files' : 'file'} to
                    create draft posts. You will receive a notification once the drafts are ready
                    for review.
                  </Typography>
                  <Link to={DRAFTS}>
                    <Button variant="contained">Go to Drafts</Button>
                  </Link>
                </>
              ) : (
                <>
                  <CircularProgressWithLabel
                    sx={(theme) => ({
                      color: theme.palette.custom.pirateGold,
                      '& svg': { overflow: 'visible' },
                      '& svg circle': { strokeWidth: 5, strokeLinecap: 'round' },
                    })}
                    size={50}
                    value={avgProgressValue}
                  />
                  <Typography
                    sx={(theme) => ({
                      mt: 2,
                      color: theme.palette.custom.tuna,
                      fontWeight: 700,
                      fontSize: 16,
                    })}
                  >
                    {avgProgressValue > 50
                      ? `Parsing your ${attachments.length > 1 ? 'files' : 'file'}...`
                      : `Uploading your ${attachments.length > 1 ? 'files' : 'file'}...`}
                  </Typography>
                </>
              )}
            </Box>
          }
        />
      )}
      {openPostsCountModal && (
        <SelectPostsCountModal
          open={openPostsCountModal}
          setOpen={setOpenPostsCountModal}
          onSubmit={({ isOnePostSelected }) => {
            onParseFile({ isOnePostSelected });
            setOpenPostsCountModal(false);
          }}
        />
      )}
    </>
  );
}

export default ParseFileActions;
